const TIB_PATTERN = /tib:\/\/[^/]+\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/gi
const tibNameCache = {}
let tibNameListener = null
let unauthorizedHandler = null

export default class ChatApiService {
  constructor(domain, session) {
    this.domain = domain || 'default'
    this.session = session || {}
  }

  static setTibNameListener(fn) {
    tibNameListener = fn
  }

  static onUnauthorized(fn) {
    unauthorizedHandler = fn
  }

  static resolveTibNames(msgs) {
    const uuids = new Set()
    for (const m of msgs) {
      const text = m.html || m.text || ''
      for (const match of text.matchAll(TIB_PATTERN)) {
        const uuid = match[1]
        if (!tibNameCache[uuid]) uuids.add(uuid)
      }
    }

    for (const uuid of uuids) {
      tibNameCache[uuid] = '...'
      fetch(`/tt/api/rest/tibtix/tib/${uuid}`, { credentials: 'include' })
          .then(r => r.ok ? r.json() : null)
          .then(data => {
            if (data?.name) {
              tibNameCache[uuid] = data.name
              if (tibNameListener) tibNameListener(uuid, data.name)
            }
          })
          .catch(() => {})
    }
  }

  static replaceTibLinks(text) {
    if (!text) return text
    return text.replace(TIB_PATTERN, (match, uuid) => {
      const name = tibNameCache[uuid] || '...'
      return `[tib:${name}](/tt/ib/${uuid})`
    })
  }

  static async checkLogin() {
    const res = await fetch('/tt/api/tibtix.com/login', {
      credentials: 'include'
    })

    if (!res.ok) return null

    const data = await res.json()

    if (!data.loggedIn) return null

    let roles = Array.isArray(data.roles) ? data.roles : []
    if (!roles.length) {
      try {
        const rolesRes = await fetch('/tt/api/userRoles', { credentials: 'include' })
        if (rolesRes.ok) {
          const parsed = await rolesRes.json()
          if (Array.isArray(parsed)) roles = parsed
        }
      } catch (e) { /* ignore */ }
    }

    // Single source of truth for the client/domain distinction:
    // the user is a client iff they do NOT have the domain-user role.
    const clientLogin = !roles.includes('domain-user')

    return {
      username: data.username,
      domain: (data.domainName || data.domain || '').includes('#') ? 'default' : (data.domainName || data.domain),
      domainAccountName: data.accountName || data.domainAccountName,
      clientLogin,
      roles,
      loggedInWithTempPassword: !!data.loggedInWithTempPassword
    }
  }

  static async changePassword(domain, oldPassword, newPassword) {
    const res = await fetch(`/tt/api/${encodeURIComponent(domain || 'tibtix.com')}/login/changePassword`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ oldPassword, newPassword })
    })
    if (!res.ok) {
      let message = `HTTP ${res.status}`
      try {
        const body = await res.json()
        if (body && typeof body.message === 'string') message = body.message
      } catch (e) { /* ignore */ }
      throw new Error(message)
    }
    return res.json().catch(() => true)
  }

  baseConnectionUrl() {
    return `/tt/api/rest/${this.domain}/connection`
  }

  chatControllerUrl() {
    return `/tt/api/${this.domain}/chat`
  }

  async request(url, options = {}) {
    const res = await fetch(url, {
      credentials: 'include', // 🔥 wichtig
      headers: {
        'Content-Type': 'application/json'
      },
      ...options
    })

    if (res.status === 401) {
      if (unauthorizedHandler) unauthorizedHandler()
      throw new Error('Unauthorized')
    }

    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`)
    }

    return res.json()
  }

  // 🔥 Connections laden (ChatController /chatConnections -> ChatConnectionUiModel)
  async getConnections() {
    const data = await this.request(`${this.chatControllerUrl()}/chatConnections`)

    return this.mapConnections(data)
  }

  // 🔥 Messages laden
  async getMessages(connectionId, index = -1, count = 20) {
    const data = await this.request(
        `${this.baseConnectionUrl()}/${connectionId}/chat/messages?index=${index}&count=${count}`
    )

    return this.mapMessages(data)
  }

  // 🔥 Nachricht senden
  async sendMessage(connectionId, text) {
    const data = await this.request(
        `${this.baseConnectionUrl()}/${connectionId}/chat/messages`,
        {
          method: 'POST',
          body: JSON.stringify({ text })
        }
    )

    return this.mapMessages(data)
  }

  // 🔥 Nachricht mit Datei-Anhang senden (multipart/form-data).
  // Backend legt die Datei via WebDavManager im Upload-Ordner ab und postet eine echte
  // Chat-Nachricht, die die Datei als tib://<domain>/<tag> referenziert. Response: Array
  // von Message-Modellen wie sendMessage. KEIN Content-Type-Header setzen (Boundary!).
  async sendMessageWithFile(connectionId, text, file) {
    const fd = new FormData()
    fd.append('file', file)
    fd.append('text', text || '')
    const url = `${this.baseConnectionUrl()}/${encodeURIComponent(connectionId)}/chat/messages/file`
    const res = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      body: fd
    })
    if (res.status === 401) {
      if (unauthorizedHandler) unauthorizedHandler()
      throw new Error('Unauthorized')
    }
    // Upload-Gate: ein Client ohne gesetztes Passwort darf keine Dateien hochladen.
    // Den Sonderfall typisiert melden, damit der Aufrufer den PasswordSetupDialog zeigen kann.
    if (res.status === 403) {
      let code = ''
      try {
        const body = await res.json()
        code = body?.error || ''
      } catch (e) { /* ignore */ }
      if (code === 'PASSWORD_REQUIRED') {
        const err = new Error('PASSWORD_REQUIRED')
        err.code = 'PASSWORD_REQUIRED'
        throw err
      }
      throw new Error(`HTTP 403`)
    }
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    return this.mapMessages(await res.json())
  }

  // Eigene Chat-Nachricht editieren (nur origin === 'CHAT'). Antwort = aktualisierte
  // Nachrichtenseite (wie getMessages). 403 = nicht eigene / nicht editierbare Nachricht.
  async editMessage(connectionId, messageId, text) {
    const fd = new URLSearchParams()
    fd.append('text', text)
    const res = await fetch(
        `${this.baseConnectionUrl()}/${connectionId}/chat/messages/${encodeURIComponent(messageId)}/edit`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: fd.toString()
        }
    )
    if (res.status === 403) {
      const err = new Error('FORBIDDEN')
      err.code = 'FORBIDDEN'
      throw err
    }
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    return this.mapMessages(await res.json())
  }

  // Eigene Nachricht löschen (Hard-Delete, jede Herkunft). Antwort = aktualisierte Seite.
  async deleteMessage(connectionId, messageId) {
    const res = await fetch(
        `${this.baseConnectionUrl()}/${connectionId}/chat/messages/${encodeURIComponent(messageId)}/delete`,
        { method: 'POST', credentials: 'include' }
    )
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    return this.mapMessages(await res.json())
  }

  // ── Passwortloses Onboarding ─────────────────────────────────────────────
  // Session läuft über das JSESSIONID-Cookie (credentials: 'include'). Nach /temp/start
  // bzw. nach einem Login ist der Benutzer als Client eingeloggt und alle weiteren Calls
  // nutzen dieselbe Session.

  // Anonymen Chat starten (erste Nachricht eines nicht eingeloggten Besuchers).
  // domainAccount = "<account>##<domain>" (Pflicht), message optional.
  async startTempChat(domainAccount, message) {
    const fd = new URLSearchParams()
    fd.append('domainAccount', domainAccount)
    if (message) fd.append('message', message)
    const res = await fetch('/tt/api/chat/temp/start', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: fd.toString()
    })
    if (!res.ok) {
      // 404 guest_chat_unavailable (unbekannt/abgeschaltet), 429 rate_limited (zu viele Starts von dieser IP)
      const err = new Error(`HTTP ${res.status}`)
      err.status = res.status
      throw err
    }
    return res.json() // { connectionId, anonId } — Sitzung über das Cookie, keine sessionId (2026-10-23)
  }

  // Nach erfolgreichem Login: anonymen Chat dem Konto zuordnen. connectionId bleibt gleich.
  async claimTempChat(anonId) {
    const fd = new URLSearchParams()
    fd.append('anonId', anonId)
    const res = await fetch('/tt/api/chat/temp/claim', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: fd.toString()
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    return res.json() // { claimed }
  }

  // 6-stelligen E-Mail-Code anfordern. Antwort immer neutral (keine Account-Enumeration).
  async requestEmailCode(email) {
    const fd = new URLSearchParams()
    fd.append('email', email)
    const res = await fetch('/tt/api/default/login/requestEmailCode', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: fd.toString()
    })
    return res.ok ? res.json() : { status: 'CODE_SENT' }
  }

  // Login per E-Mail-Code (passwortlos). Pfad-Segment 'default' erzwingt einen CLIENT-Login.
  async loginWithEmailCode(email, code) {
    const fd = new URLSearchParams()
    fd.append('email', email)
    fd.append('code', code)
    const res = await fetch('/tt/api/default/login/emailCode', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: fd.toString()
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    return res.json() // LoginResponseModel
  }

  // Login-Optionen prüfen — steuert, welche Felder der LoginDialog zeigt.
  async loginOptions(email) {
    const res = await fetch(
        `/tt/api/default/login/options?email=${encodeURIComponent(email)}`,
        { credentials: 'include' }
    )
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    return res.json()
  }

  // Passwort setzen (oldPassword null/leer = Erst-Setzung). Danach passwordConfigured=true.
  async setPassword(newPassword) {
    const res = await fetch('/tt/api/default/login/changePassword', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ oldPassword: null, newPassword })
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    return res.json().catch(() => true)
  }

  // 🧠 Mapping

  mapConnections(connections) {
    const clientLogin = !!this.session.clientLogin
    return connections.map(c => {
      const enc = encodeURIComponent
      let name
      let imageUrl
      if (clientLogin) {
        // Client is logged in -> partner is a domain account
        name = c.displayName || c.domainAccountName
        imageUrl = `/tt/api/image/${enc(c.domainAccountName)}/${enc(c.domain)}/account`
      } else {
        // Domain user is logged in -> partner is a client user
        name = c.displayName || c.clientIdentifier
        imageUrl = c.userId ? `/tt/api/image/${enc(c.userId)}/user` : ''
      }
      return {
        id: c.connectionId,
        name,
        imageUrl,
        domainAccountName: c.domainAccountName,
        clientAccountName: c.domainAccountName,
        hashhashDomain: c.domain,
        clientIdentifier: c.clientIdentifier,
        // For client connections the backend uses clientIdentifier as the email;
        // prefer an explicit `email` field if the API ever exposes one.
        email: c.email || c.clientIdentifier,
        userId: c.userId,
        number: c.number,
        flags: c.flags,
        path: c.path,
        open: c.open,
        unread: c.unread,
        clientUnreadCount: c.clientUnreadCount ?? 0,
        domainUnreadCount: c.domainUnreadCount ?? 0,
        clientLastViewed: c.clientLastViewed,
        domainLastViewed: c.domainLastViewed,
        version: c.version,
        chatNotes: c.chatNotes
      }
    })
  }

  mapMessages(messages) {
    return messages.map(m => ({
      id: m.id,
      index: m.index,
      status: m.status,
      text: m.text,
      html: m.textHtml,
      isOwn: m.isOwn,
      name: m.displayName,
      image: m.imageUrl,
      date: new Date(m.date),
      // origin steers the edit affordance: only "CHAT" messages can be edited.
      origin: m.origin
    }))
  }

  // Profile of the client user behind a connection. The backend resolves the user via
  // `connection.clientIdentifier` (= email), so a client can call this on any of their
  // own connections to fetch/update their own profile.
  async getConnectionUser(connectionId) {
    return await this.request(
        `${this.baseConnectionUrl()}/${encodeURIComponent(connectionId)}/getConnectionUser`
    )
  }

  // Backend uses @RequestParam, so we send form-encoded data instead of JSON.
  // Only fields with non-null values are sent; missing fields stay unchanged server-side.
  async updateConnectionUser(connectionId, fields) {
    const params = new URLSearchParams()
    for (const [k, v] of Object.entries(fields || {})) {
      if (v != null) params.append(k, String(v))
    }
    const url = `${this.baseConnectionUrl()}/${encodeURIComponent(connectionId)}/updateConnectionUser`
    const res = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: params.toString()
    })
    if (res.status === 401) {
      if (unauthorizedHandler) unauthorizedHandler()
      throw new Error('Unauthorized')
    }
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    return res.json().catch(() => ({}))
  }

  // Domain user only: creates a new client account by email and starts a chat with them.
  // The server sends the new client an email prompting them to set a password.
  // `app` is forwarded to the email template (kept as a constant here; change if your
  // backend expects a specific app identifier other than 'chat').
  async addClient({ firstName, lastName, email, message }) {
    return await this.request(`${this.baseConnectionUrl()}/addClient`, {
      method: 'POST',
      body: JSON.stringify({
        firstName,
        lastName,
        email,
        accountCreationMessage: message || '',
        app: 'chat'
      })
    })
  }

  // Returns the MQTT broker configuration so the UI can subscribe to chat-change events.
  // Shape: { configured: boolean, host: string, port: number, topicBase: string }
  async getMqttConfig() {
    try {
      const res = await fetch('/tt/api/rest/tibtix/mqttConfig', { credentials: 'include' })
      if (!res.ok) return null
      return res.json()
    } catch (e) {
      return null
    }
  }

  // Upload a new profile image for the currently authenticated user.
  // Endpoint lives in TibtixRestApiController (base /api/rest/tibtix).
  // Uses multipart/form-data; do NOT reuse the JSON `request` helper.
  async updateUserImage(file) {
    const fd = new FormData()
    fd.append('image', file)
    const url = `/tt/api/rest/tibtix/updateUserImage`
    const res = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      body: fd
    })
    if (res.status === 401) {
      if (unauthorizedHandler) unauthorizedHandler()
      throw new Error('Unauthorized')
    }
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    return res.json().catch(() => ({}))
  }

  // Server-managed: resets lastViewed. Without timestamp the server uses current server time;
  // with timestamp, lastViewed is set to the given value (epoch ms of the acknowledged message).
  async resetChatLastViewed(connectionId, timestamp) {
    const base = `${this.baseConnectionUrl()}/${encodeURIComponent(connectionId)}/chat/messages/resetChatLastViewed`
    const url = timestamp != null ? `${base}?timestamp=${encodeURIComponent(timestamp)}` : base
    try {
      await this.request(url)
    } catch (e) {
      /* best-effort */
    }
  }
}
